/**
 * Utility functions for extracting static values from expressions in edit mode
 */

/**
 * Extracts a static value from an expression or returns the value as-is
 * Used in edit mode to display static content without evaluating expressions
 */
export function extractStaticValue(value: any): any {
    if (value == null) return undefined;

    // If it's already a primitive, return it
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }

    // Handle expression objects
    if (typeof value === 'object' && 'Type' in value) {
        const expr = value as any;

        // For Const expressions, return the Value
        if (expr.Type === 'Const' && 'Value' in expr) {
            return expr.Value;
        }

        // For other expressions, return a placeholder
        return undefined;
    }

    // For arrays or objects, try to extract first item
    if (Array.isArray(value) && value.length > 0) {
        return extractStaticValue(value[0]);
    }

    return undefined;
}

/**
 * Extracts a static string value, with fallback
 */
export function extractStaticString(value: any, fallback: string = ''): string {
    const extracted = extractStaticValue(value);
    if (typeof extracted === 'string') return extracted;
    if (typeof extracted === 'number' || typeof extracted === 'boolean') return String(extracted);
    return fallback;
}

/**
 * Generates mock data for tables/lists in edit mode
 */
export function generateMockData(count: number = 3): any[] {
    return Array.from({ length: count }, (_, i) => ({
        id: i + 1,
        name: `Sample Item ${i + 1}`,
        status: i % 2 === 0 ? 'Active' : 'Pending',
        value: Math.floor(Math.random() * 1000),
        date: new Date().toLocaleDateString(),
    }));
}

/**
 * Checks if a component is selected based on its ID and current selection path
 * Note: selectedPath can be null (representing view root) or an array
 * currentPath is always an array (empty array [] represents view root)
 */
export function isComponentSelected(componentId: string, selectedPath: string[] | null, currentPath: string[]): boolean {
    // Both null/empty represent the view root - these should match
    const selectedIsViewRoot = !selectedPath || selectedPath.length === 0;
    const currentIsViewRoot = currentPath.length === 0;

    if (selectedIsViewRoot && currentIsViewRoot) {
        return true;
    }

    // If only one is view root, they don't match
    if (selectedIsViewRoot || currentIsViewRoot) {
        return false;
    }

    // Both are non-empty paths - compare them element by element
    if (selectedPath.length !== currentPath.length) {
        return false;
    }

    return selectedPath.every((v, i) => v === currentPath[i]);
}

/**
 * Builds CSS class names for edit mode
 */
export function getEditModeClasses(options: {
    editMode: boolean;
    isSelected: boolean;
    isContainer?: boolean;
    isEmpty?: boolean;
}): string {
    if (!options.editMode) return '';

    const classes = ['iw-edit-mode'];

    if (options.isSelected) classes.push('iw-selected');
    if (options.isContainer) classes.push('iw-container');
    if (options.isEmpty) classes.push('iw-empty');

    return classes.join(' ');
}
